import { Checkbox, SingleSelectDropdown, UniversityDropdown } from "@components/library";
import { DropdownOption } from "@components/library/Dropdowns/DropdownOption";
import { COUNTRY_OPTIONS } from "@constants";
import { getDisciplines } from "@requests/keywords";
import { useQuery } from "@tanstack/react-query";
import { getOptionFromValue, getValueFromOption } from "@utils/dropdownUtils";
import { Dispatch, SetStateAction, useEffect } from "react";
import api from "src/requests/request";
import { BaseOnboardingProfileInfo } from "../OnboardingContainer";
import * as S from "../StepStyles";

export type UniversityAdminOnboardingProfileInfo = BaseOnboardingProfileInfo &
  Partial<{
    disciplines?: string[];
    universityHasCountry?: boolean;
    country_code?: string | undefined;
  }>;

interface Props {
  profileInfo: UniversityAdminOnboardingProfileInfo;
  setProfileInfo: Dispatch<SetStateAction<UniversityAdminOnboardingProfileInfo>>;
  prefillInstitutionName?: string;
  isPrefillReadonly?: boolean;
}

function ResearchInstitutionAdmin({
  profileInfo,
  setProfileInfo,
  prefillInstitutionName = "",
  isPrefillReadonly = true,
}: Props) {
  const { data: disciplineOptions } = useQuery({
    queryKey: ["getDisciplines"],
    queryFn: () => getDisciplines(),
    select: (data) => data.map(getOptionFromValue) as DropdownOption[],
  });

  useEffect(() => {
    if (!disciplineOptions) return;
    if ("disciplines" in profileInfo) return;
    setProfileInfo({
      ...profileInfo,
      disciplines: disciplineOptions.map(getValueFromOption) as string[],
    });
  }, [disciplineOptions]);

  const { university, universityHasCountry, country_code, disciplines } = profileInfo;

  const handleDisciplines = (value) => {
    setProfileInfo({
      ...profileInfo,
      disciplines: disciplines?.includes(value)
        ? disciplines.filter((v) => v !== value)
        : [...(disciplines ?? []), value],
    });
  };

  const handleSelectUniversityOption = async (option) => {
    if (!option) return;

    const universityName = option.label;

    const responseJson = await api.get("/universities/has_country", {
      params: { name: universityName },
    });

    const { has_country } = responseJson.data;

    setProfileInfo({
      ...profileInfo,
      university: universityName,
      universityHasCountry: has_country,
    });
  };

  const handleAllDisciplines = (event) => {
    if (event.target.checked) {
      setProfileInfo({
        ...profileInfo,
        disciplines: (disciplineOptions ?? []).map((option) => option?.value),
      });
    } else {
      setProfileInfo({
        ...profileInfo,
        disciplines: [],
      });
    }
  };

  return (
    <>
      <S.DropdownRow>
        <S.DropdownTitle>Research institution</S.DropdownTitle>
        {isPrefillReadonly && prefillInstitutionName.length > 0 ? (
          <S.Input type="text" value={prefillInstitutionName} tabIndex={-1} fixed readOnly />
        ) : (
          <>
            <UniversityDropdown
              value={getOptionFromValue(university)}
              onChange={handleSelectUniversityOption}
              defaultInputValue={prefillInstitutionName}
              data-testid="ri-admin-institution-name-input"
            />
            <S.ContactLink>
              You can{" "}
              <a
                href="/contact?message=Hello, I can't find my institution as an option. My institution is: "
                target="_blank"
              >
                request that we add your institution
              </a>{" "}
              if you don&rsquo;t see it above.
            </S.ContactLink>
          </>
        )}
      </S.DropdownRow>
      {!universityHasCountry && (!isPrefillReadonly || prefillInstitutionName.length === 0) && (
        <S.DropdownRow>
          <S.DropdownTitle>Institution location</S.DropdownTitle>
          <SingleSelectDropdown
            placeholder="Search countries"
            value={getOptionFromValue(country_code)}
            options={COUNTRY_OPTIONS}
            onChange={(option) =>
              setProfileInfo((oldProfileInfo) => ({
                ...oldProfileInfo,
                country_code: getValueFromOption(option),
              }))
            }
            data-testid="ri-admin-institution-location-input"
          />
        </S.DropdownRow>
      )}
      <S.DropdownRow>
        <S.DropdownTitle>Which topics would you like to hear about on Halo?</S.DropdownTitle>
        <S.CheckboxContainer>
          <S.CheckboxItem>
            <Checkbox
              label="All disciplines"
              isChecked={disciplines?.length === disciplineOptions?.length}
              onChange={handleAllDisciplines}
              size="sm"
            />
          </S.CheckboxItem>
          {disciplineOptions?.map((option) => (
            <S.CheckboxItem key={option?.value}>
              <Checkbox
                label={option?.label}
                isChecked={Boolean(disciplines?.includes(option?.value))}
                onChange={() => handleDisciplines(option?.value)}
                size="sm"
              />
            </S.CheckboxItem>
          ))}
        </S.CheckboxContainer>
      </S.DropdownRow>
    </>
  );
}

export default ResearchInstitutionAdmin;
